import { useEffect } from 'react'

import {
  isChunkLoadError,
  reloadOnceForChunkError,
} from '@/shared/lib/chunk-recovery'

interface RouteErrorBoundaryProps {
  error: unknown
}

type Lang = 'pt-BR' | 'en'

const MESSAGES: Record<
  Lang,
  { title: string; description: string; action: string }
> = {
  'pt-BR': {
    title: 'Não foi possível carregar esta página',
    description:
      'A aplicação foi atualizada ou a conexão falhou. Recarregue para continuar.',
    action: 'Recarregar',
  },
  en: {
    title: 'This page could not be loaded',
    description:
      'The app was updated or the connection failed. Reload to continue.',
    action: 'Reload',
  },
}

function resolveLang(): Lang {
  const lang =
    typeof document !== 'undefined' ? document.documentElement.lang : ''
  return lang.toLowerCase().startsWith('en') ? 'en' : 'pt-BR'
}

export function RouteErrorBoundary({ error }: RouteErrorBoundaryProps) {
  useEffect(() => {
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError()
    }
  }, [error])

  const t = MESSAGES[resolveLang()]

  return (
    <div
      role="alert"
      data-testid="route-error-recovery"
      className="flex min-h-svh flex-1 flex-col items-center justify-center gap-4 p-8 text-center"
    >
      <div className="space-y-2">
        <h1 className="text-lg font-semibold text-[color:var(--color-foreground,#1a1a1a)]">
          {t.title}
        </h1>
        <p className="max-w-md text-sm text-[color:var(--color-muted-foreground,#6b7280)]">
          {t.description}
        </p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-[color:var(--color-primary,#1a1a1a)] px-4 py-2 text-sm font-medium text-[color:var(--color-primary-foreground,#ffffff)]"
      >
        {t.action}
      </button>
    </div>
  )
}
